import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import AgenticOnboardingAssistant from "../components/AgenticOnboardingAssistant";
import { authClient } from "../lib/auth-client";
import { apiFetch } from "../lib/api-client";
import {
  fetchOnboardingProgress,
  type AgenticOnboardingAgentState,
  type AgenticOnboardingProgress,
} from "../lib/onboarding";
import type { RootLayoutContext } from "../layouts/RootLayout";

type MeResponse = {
  data: {
    applicationUser: {
      companyId: string;
      companyName: string;
      isOwner: boolean;
    } | null;
  };
};

function titleFromId(id: string) {
  return id
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function SetupProgress({
  progress,
}: {
  progress: AgenticOnboardingProgress | null;
}) {
  if (!progress) {
    return (
      <aside
        aria-busy="true"
        className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#2f7e8b]">
          Saved setup progress
        </p>
        <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
          Start operating quickly
        </h2>
        <p className="mt-5 text-sm text-slate-500">Loading your saved progress…</p>
      </aside>
    );
  }

  return (
    <aside className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#2f7e8b]">
        Saved setup progress
      </p>
      <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
        Start operating quickly
      </h2>
      <div className="mt-5 space-y-6">
        {progress?.setups.map((setup) => (
          <section key={setup.setup.id}>
            <div className="flex items-center gap-2">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                  setup.status === "completed"
                    ? "bg-[#2f7e8b] text-white"
                    : setup.status === "active" ||
                        setup.status === "awaiting_approval"
                      ? "border-2 border-[#2f7e8b] text-[#2f7e8b]"
                      : "border border-slate-300 text-slate-400"
                }`}
              >
                {setup.status === "completed" ? "✓" : ""}
              </span>
              <h3 className="text-sm font-semibold text-slate-900">
                {titleFromId(setup.setup.id)}
              </h3>
            </div>
            <ol className="ml-7 mt-3 space-y-2">
              {setup.setup.goals.map((goal) => {
                const complete =
                  setup.status === "completed" ||
                  setup.savedVariables.includes(goal.variable);
                return (
                  <li
                    key={goal.id}
                    className="flex gap-2 text-xs leading-5 text-slate-600"
                  >
                    <span
                      className={
                        complete ? "text-[#2f7e8b]" : "text-slate-300"
                      }
                    >
                      {complete ? "✓" : "○"}
                    </span>
                    <span>
                      {goal.goal}
                      {goal.required === false ? " (Optional)" : ""}
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </aside>
  );
}

export function membershipFromProgress(
  progress: AgenticOnboardingProgress,
): boolean {
  return progress.hasCompany;
}

function ManualCompanyForm({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await apiFetch("/api/v1/companies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), country: country.trim(), timezone }),
      });
      if (!response.ok) {
        setError("We couldn’t create your company. Review the details and try again.");
        return;
      }
      onCreated();
    } catch {
      setError("We couldn’t reach the service. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto w-full max-w-md">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 text-sm font-medium text-[#2f7e8b]"
      >
        ← Back to guided setup
      </button>
      <form
        onSubmit={submit}
        className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6"
      >
        <h2 className="text-xl font-semibold text-slate-950">Create your company</h2>
        <label className="block text-sm font-medium text-slate-700">
          Company name
          <input
            required
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3"
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Country
          <input
            required
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3"
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Timezone
          <input
            required
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3"
          />
        </label>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          disabled={submitting}
          className="w-full rounded-lg bg-[#173f5f] px-4 py-3 font-semibold text-white disabled:opacity-60"
        >
          {submitting ? "Creating…" : "Create company"}
        </button>
      </form>
    </section>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { onRemoveCompany, isRemovingCompany } =
    useOutletContext<RootLayoutContext>();
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState<AgenticOnboardingProgress | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [hasMembership, setHasMembership] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setError("");
    try {
      const meResponse = await apiFetch("/api/v1/me");
      if (meResponse.status === 401) {
        navigate("/auth", { replace: true });
        return;
      }
      if (!meResponse.ok) throw new Error("Unable to load workspace");
      const me = (await meResponse.json()) as MeResponse;
      if (me.data.applicationUser && !me.data.applicationUser.isOwner) {
        navigate("/", { replace: true });
        return;
      }
      setHasMembership(Boolean(me.data.applicationUser));
      const nextProgress = await fetchOnboardingProgress();
      setProgress(nextProgress);
      setSetupComplete(nextProgress.complete);
    } catch {
      setError("We couldn’t load onboarding progress. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, [navigate, user]);

  useEffect(() => {
    if (isPending) return;
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }
    void load();
  }, [isPending, load, navigate, user]);

  const handleAgentState = useCallback(
    (state: AgenticOnboardingAgentState | undefined) => {
      if (!state?.progress) return;
      setProgress(state.progress);
      setSetupComplete(state.progress.complete);
      setHasMembership(membershipFromProgress(state.progress));
    },
    [],
  );

  if (isPending || loading || !user) {
    return (
      <main className="flex min-h-[calc(100vh-73px)] items-center justify-center px-6 text-sm text-slate-500">
        Loading onboarding…
      </main>
    );
  }

  if (showManual) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-14">
        <ManualCompanyForm
          onBack={() => setShowManual(false)}
          onCreated={() => {
            setShowManual(false);
            void load();
          }}
        />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-10 sm:px-10 sm:py-14">
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#2f7e8b]">
          WorkView Agent
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
          Set up by doing real work
        </h1>
        <p className="mt-3 text-slate-600">
          Create your company, add the practical operating and commercial
          basics, then create the first customer, property, and job. Your
          progress resumes after refresh or reconnect.
        </p>
      </div>

      {error && (
        <p className="mx-auto mt-6 max-w-3xl rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      {!hasMembership && (
        <div className="mx-auto mt-8 max-w-5xl text-center">
          <button
            type="button"
            onClick={() => setShowOptions((value) => !value)}
            className="text-sm font-medium text-slate-700 underline"
          >
            {showOptions ? "Hide more setup options" : "More setup options"}
          </button>
          {showOptions && (
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowManual(true)}
                className="border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
              >
                Use standard company form
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mx-auto mt-8 grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div>
          <p className="mb-2 px-1 text-sm font-medium text-slate-600">
            Write Hello to start
          </p>
          <AgenticOnboardingAssistant
            authUserId={user.id}
            onStateChange={handleAgentState}
            onComplete={() => setSetupComplete(true)}
          />
          {setupComplete && (
            <section className="mt-4 border border-slate-200 bg-white p-5">
              <p className="text-sm font-semibold text-slate-950">Initial setup is complete.</p>
              <p className="mt-1 text-sm text-slate-600">Your workspace is ready. Open it when you’re ready to start operating.</p>
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="mt-4 border border-slate-900 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
              >
                Open workspace
              </button>
            </section>
          )}
        </div>
        <div className="space-y-4">
          <SetupProgress progress={progress} />
          <button
            type="button"
            onClick={onRemoveCompany}
            disabled={isRemovingCompany}
            className="w-full text-center text-sm font-medium text-red-600 underline disabled:opacity-60"
          >
            {isRemovingCompany
              ? hasMembership
                ? "Removing company…"
                : "Clearing setup…"
              : "Remove company"}
          </button>
        </div>
      </div>
    </main>
  );
}
